import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import * as d3 from 'd3';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationService } from "../../services/configuration.service";

export interface IStackedKeys {
  name: string;
  height: string;
  label?: string;
}
export interface IStackedConfig {
  w?: number;
  h?: number;
  colors?: string[];
  stackedKeys?: IStackedKeys[];
  yRange?: number[];
  rectWidth?: number;
  yAxisFormat?: string;
  xProperty?: string;
  tooltipTitle?: string;
  chartClass?: string;
  hideXaxis?: boolean;
}
@Component({
    selector: 'dynamica-stacked-bar',
    templateUrl: './stacked-bar.component.html',
    styleUrls: ['./stacked-bar.component.scss']
})
export class StackedBarComponent implements OnChanges {
  
  private configurationService = inject(ConfigurationService);
  private translateService = inject(TranslateService);

  @ViewChild('chartContainer', { static: true }) container: ElementRef;
  @ViewChild('chartParent', { static: true }) chartParent: ElementRef;
  @Output() selectedValue = new EventEmitter();
  @Input() chartConfig: IStackedConfig = {};
  @Input() dataSet: any = [];
  private margin = { top: 30, right: 50, bottom: 60, left: 90 };
  private width;
  private height;

  private x: any;
  private y: any;
  private svg: any;
  private parent: any;
  private tooltip: any;
  private activedRect: any;
  private chart: any;
  private tooltipChart: any;
  private pointData: any = {};

  private stackedSeries: any;
  private layersBar: any;
  private layerBar: any;
  private xAxis: any;
  private yAxis: any;
  ngOnChanges(changes: SimpleChanges): void {
    if ( changes['chartConfig'] && changes['chartConfig'].currentValue) {
      this.width = this.chartConfig.w! - this.margin.left - this.margin.right;
      this.height = this.chartConfig.h! - this.margin.top - this.margin.bottom;
    }
    if ( changes['dataSet'] && changes['dataSet'].currentValue) {
      const svg = d3.select(this.container.nativeElement);
      const div = d3.select(this.chartParent.nativeElement);

      svg.selectAll('g').remove();
      div.selectAll('div').remove();

      this.initScales();
      this.initSvg();
      this.createStack(this.dataSet);
    }
  }

  private initScales(): void {
    this.x = d3.scaleBand()
      .rangeRound([0, this.width])
      .padding(0.05);

    this.y = d3.scaleLinear()
      .range([this.height, 0]);
  }

  private initSvg(): void {

    this.svg = d3.select(this.container.nativeElement)
      .attr('class', 'chart')
      .attr('width', '100%')
      .attr('height', this.chartConfig.h);
    this.parent = d3.select(this.chartParent.nativeElement);
  }

  private createStack(stackData: any): void {
    if (stackData.length > 0) {
      this.stackedSeries = stackData;

      this.x.domain(this.dataSet.map((d: any) => d[this.chartConfig.xProperty!]));
      const yRange = [];
      for (const element of this.chartConfig.stackedKeys!) {
        yRange.push(Math.max(...this.stackedSeries.map((o) => o[element.name])));
      }
      const max = yRange.reduce((a, b) => {
        return Math.max(a, b);
      }, 1);
      this.y.domain([0, max]);
      this.drawChart(this.stackedSeries);
    }
  }

  private drawChart(data: any): void {
    const configCurrency = this.chartConfig.yAxisFormat === 'currency' ? this.configurationService.currency : '';
    this.chart = this.svg.append('g')
      .classed(this.chartConfig.chartClass, true)
      .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')');
    this.activedRect = this.chart.append('rect').classed('activated-layout', true);

    const xAxis = d3.axisBottom(this.x);
    const formatPercent = d3.format('.0%');
    const yAxis = d3.axisLeft(this.y)
      .ticks(5)
      .tickSize(-innerWidth)
      .tickPadding(7);
    if (this.chartConfig.yAxisFormat) {

      switch (this.chartConfig.yAxisFormat) {
        case 'percentage':
          yAxis.tickFormat(formatPercent);
          break;
        default:
          yAxis.tickFormat((d: any) => d3.format(',.0f')(d) + configCurrency);
          break;
      }
    }
    if (!this.chartConfig.hideXaxis) {
      this.xAxis = this.chart.append('g')
        .call(xAxis)
        .classed('xAxis', true)
        .attr('transform', 'translate(0,' + this.height + ')');
    }


    this.yAxis = this.chart.append('g')
      .classed('yAxis', true)
      .call(yAxis);
    this.tooltip = this.parent
      .append('div')
      .attr('class', 'chart-tooltip-stacked')
      .style('opacity', 0);
    this.layersBar = this.chart.selectAll('.layers')
      .append('g')
      .attr('class', 'layers')
      .data(data)
      .enter();
    this.layerBar = this.layersBar.append('g')
      .attr('class', (_d, i) => {
        return 'layer' + i;
      })
      .attr('width', this.x.bandwidth())
      .attr('height', this.height)
      .append('g')
      .attr('clip-path', (_d) => {
        return 'inset(0px 0px 0px 0px round 8px 8px 8px 8px)';
      });

    this.layersBar.append('rect').classed('active-rect', true)
      .attr('y', 0)
      .attr('x', (d: any, _i: any) => {
        return this.x(d[this.chartConfig.xProperty!]) + (this.chartConfig.rectWidth! / 2);
      }).attr('width', this.x.bandwidth())
      .attr('height', this.height + 20).style('fill', 'transparent')
      .on('click', (e, d, _idx) => {
        rectClick(e, d);
      })
      .on('mouseover', (e, d) => mouseover(e, d))
      .on('mouseleave', (e, d) => mouseleave(e, d));
    for (let index = 0; index < this.chartConfig.stackedKeys!.length; index++) {
      const element = this.chartConfig.stackedKeys![index];

      this.layerBar.append('rect')
        .attr('y', (d: any) => {
          if (d[element.name] !== null) { return this.y(d[element.name]); }
          return;
        })
        .attr('x', (d: any, _i: any) => {
          return this.x(d[this.chartConfig.xProperty!]) + (this.chartConfig.rectWidth! / 2);
        }).style('fill', (_d: any, _i: any) => {
          return this.chartConfig.colors![index];
        })
        .attr('width', this.x.bandwidth() - this.chartConfig.rectWidth!)
        .attr('height', (d: any, _i: any) => {
          if (d[element.name] !== null) {
            if (element.height !== 'auto') { return element.height; }
            else { return this.height - this.y(d[element.name]); }
          } else {
            return;
          }
        });
    }
    const mouseleave = (_e, _d) => {
      this.tooltip.selectAll('.tooltip-charts').remove();
      this.tooltip.selectAll('text').remove();
      this.tooltip.attr('style', null);
      this.tooltip.transition().duration(200).style('opacity', 0);

    };
    const rectClick = (event, pointData) => {
      this.activedRect.attr('x', this.x(pointData[this.chartConfig.xProperty!]))
        .attr('y', -10)
        .attr('width', this.x.bandwidth())
        .attr('height', this.height + 40)
        .attr('clip-path', (_d) => {
          return 'inset(0px 0px 0px 0px round 8px 8px 8px 8px)';
        })
        .attr('fill', '#E3E3E6');
      this.selectedValue.emit(pointData);
    };

    const mouseover = (event, pointData) => {
      this.pointData = pointData;
      this.tooltip.selectAll('.tooltip-charts').remove();
      this.tooltip.selectAll('text').remove();
      this.tooltip.attr('style', null);
      this.tooltip.attr('class', 'chart-tooltip-stacked');
      this.tooltip.transition().duration(200).style('opacity', 1);

      if (this.x(pointData[this.chartConfig.xProperty!]) <= this.width - this.x.bandwidth() * 1.5) {
        this.tooltip.style('left', (this.x(pointData[this.chartConfig.xProperty!]) + 70) + 'px')
          .style('top', '40px')
          .style('width', (this.x.bandwidth() + 140) + 'px')
          .style('height', (this.height + this.margin.bottom) + 'px');
        this.tooltip.classed('flex-row-reverse', true);
      } else {
        this.tooltip
          .style(
            'right',
            this.x(pointData[this.chartConfig.xProperty!]) + 50 + this.x.bandwidth() - this.width + 'px',
          )
          .style('top', '40px')
          .style('width', this.x.bandwidth() + 140 + 'px')
          .style('height', this.height + this.margin.bottom + 'px');
        this.tooltip.classed('flex-row', true);

      }

      const tooltipDraft = this.tooltip.html(`<div class="tooltip-draft">
      <h6>${d3.timeFormat('%a %d/%m/%Y')(new Date(pointData[this.chartConfig.tooltipTitle!]))}</h6>
      </div>`);
      const draftKeys = tooltipDraft.selectAll('div');
      for (const key of this.chartConfig.stackedKeys!) {
        if (this.chartConfig.stackedKeys!.length > 1) {
          draftKeys.append('p').attr('class', 'draft-title').text(key.label);
        }
        switch (this.chartConfig.yAxisFormat) {
          case 'percentage':
            draftKeys.append('p').text(d3.format('.0%')(pointData[key.name]));
            break;
          default:
            draftKeys.append('p').text(d3.format(',.0f')(pointData[key.name]) + configCurrency);
            break;
        }
      }
      const chartTooltipParent = this.tooltip.append('svg')
        .attr('width', this.x.bandwidth() - this.chartConfig.rectWidth!)
        .attr('height', this.height + 25);

      for (let index = 0; index < this.chartConfig.stackedKeys!.length; index++) {
        const element = this.chartConfig.stackedKeys![index];
        this.tooltipChart = chartTooltipParent
          .append('rect')
          .attr('class', element)
          .attr('clip-path', (_d) => {
            return 'inset(0px 0px 0px 0px round 8px 8px 8px 8px)';
          })
          .attr('y', (_d: any) => {
            if (pointData[element.name] !== null) { return this.y(pointData[element.name]) + 10; }
            return;
          })
          .attr('x', 0)
          .style('fill', (_d: any, _i: any) => {
            return this.chartConfig.colors![index];
          })
          .attr('width', this.x.bandwidth() - this.chartConfig.rectWidth!)
          .attr('height', (_d: any, _i: any) => {
            if (pointData[element.name] !== null) {
              if (element.height !== 'auto') { return element.height; }
              else { return this.height - this.y(pointData[element.name]); }
            } else {
              return;
            }

          });
      }
      if (this.chartConfig.stackedKeys!.length > 1) {
        chartTooltipParent.append('text')
          .attr('x', (this.x.bandwidth() - this.chartConfig.rectWidth! - 15) / 2)
          .attr('y', this.height)
          .attr('dy', '2em')
          .text(pointData[this.chartConfig.xProperty!]);
      }


    };

  }


}


